<template>
  <div class="wrapper" :class="[position]">
    <span class="ribbon">{{ text }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    text?: string
    position?: 'left' | 'right'
    colour?: string // must be hexadecimal (256 bits)
    gradientLightness?: number
  }>(),
  {
    position: 'left',
    colour: '#ff1111',
    gradientLightness: -30,
  },
)

const secondaryColour = computed(() => adjustLightness(props.colour, props.gradientLightness))

function adjustLightness(hex: string, percent: number): string {
  // strip “#” and parse r,g,b
  const num = parseInt(hex.replace(/^#/, ''), 16)
  let r = (num >> 16) & 0xff
  let g = (num >> 8) & 0xff
  let b = num & 0xff

  // convert to HSL
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h = 0,
    s = 0,
    l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  // apply lightness shift
  l = Math.min(1, Math.max(0, l + percent / 100))

  // convert back to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  let r2: number, g2: number, b2: number
  if (s === 0) {
    r2 = g2 = b2 = l // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r2 = hue2rgb(p, q, h + 1 / 3)
    g2 = hue2rgb(p, q, h)
    b2 = hue2rgb(p, q, h - 1 / 3)
  }

  // back to hex
  const toHex = (x: number) => {
    const hx = Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')
    return hx
  }
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

.wrapper {
  position: absolute;
  top: -5px;
  z-index: 1;
  overflow: hidden;
  width: 100px;
  height: 100px;
  // width: fit-content;
  // height: fit-content;
  // aspect-ratio: 1;
  text-align: right;
  margin: 0;

  .ribbon {
    // position: absolute;
    position: relative;
    width: 130px;
    display: block;
    top: 29px;

    font-size: 9px;
    font-weight: bold;
    color: #fff;
    text-transform: uppercase;
    text-align: center;
    line-height: 20px;
    box-shadow: 0 3px 10px -5px rgba(0, 0, 0, 1);
    text-shadow: none;

    &::before,
    &::after {
      border: 3px solid transparent;
    }

    &::after {
      transform: rotate(90deg);
    }

    &::before {
      content: '';
      position: absolute;
      left: 0px;
      top: 100%;
      z-index: -1;
    }

    &::after {
      content: '';
      position: absolute;
      right: 0px;
      top: 100%;
      z-index: -1;
    }
  }

  .ribbon {
    background: linear-gradient(v-bind(colour) 0%, v-bind(secondaryColour));

    &::before,
    &::after {
      border-top: 3px solid v-bind(secondaryColour);
      border-left: 3px solid v-bind(secondaryColour);
    }
  }

  $ribbon-side-adjustment: -5px;

  // position variations
  &.left {
    left: $ribbon-side-adjustment;

    span {
      left: -26px;
      transform: rotate(-45deg);
    }
  }

  &.right {
    right: $ribbon-side-adjustment;

    .ribbon {
      right: 5px;
      transform: rotate(45deg);
    }
  }
}
</style>
